import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { showSuccess, showError } from '../../../utils/notifications';
import standardMessageService, { StandardMessage } from '../../../services/standardMessageService';
import reservationsService from '../../../services/reservationsService';
import housesService from '../../../services/housesService';

const MessageComposer = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState<StandardMessage[]>([]);
    const [reservations, setReservations] = useState<any[]>([]);
    const [houses, setHouses] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [copied, setCopied] = useState(false);

    const [selectedTemplate, setSelectedTemplate] = useState<number>(0);
    const [selectedReservation, setSelectedReservation] = useState<string>('');
    const [selectedLanguage, setSelectedLanguage] = useState<string>('es');
    const [generatedMessage, setGeneratedMessage] = useState<string>('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [templatesData, reservationsData, housesData] = await Promise.all([
                standardMessageService.getByType('message'),
                reservationsService.getAll(),
                housesService.getAll(),
            ]);
            setTemplates(templatesData);
            setReservations(reservationsData);
            setHouses(housesData);

            if (templatesData.length > 0) {
                setSelectedTemplate(templatesData[0].id);
            }
        } catch (err: any) {
            showError('Error!', 'Failed to load data');
            console.error('Error loading data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateMessage = async () => {
        if (!selectedTemplate) {
            showError('Error!', 'Please select a message template');
            return;
        }
        if (!selectedReservation) {
            showError('Error!', 'Please select a reservation');
            return;
        }

        try {
            setGenerating(true);

            // Find reservation and house
            const reservation = reservations.find(r => r.public_id === selectedReservation);
            if (!reservation) {
                showError('Error!', 'Reservation not found');
                return;
            }

            const house = houses.find(h => h.public_id === reservation.house_id);
            if (!house) {
                showError('Error!', 'House not found');
                return;
            }

            // Build variables
            const variables = standardMessageService.buildVariablesFromReservation(reservation, house);

            // Generate message
            const message = await standardMessageService.generateMessage({
                template_id: selectedTemplate,
                language: selectedLanguage,
                variables,
            });

            setGeneratedMessage(message);
            showSuccess('Success!', 'Message generated successfully');
        } catch (err: any) {
            showError('Error!', err.response?.data?.error || 'Failed to generate message');
            console.error('Error generating message:', err);
        } finally {
            setGenerating(false);
        }
    };

    const handleCopyMessage = () => {
        if (generatedMessage) {
            navigator.clipboard.writeText(generatedMessage);
            setCopied(true);
            showSuccess('Copied!', 'Message copied to clipboard');
            setTimeout(() => setCopied(false), 2000);
        }
    };

    if (loading) {
        return (
            <div>
                <Button variant="link" onClick={() => navigate('/cms/standard-messages')} className="mb-3 ps-0">
                    <ArrowLeft size={18} className="me-2" />
                    Back to Templates
                </Button>
                <h4 className="mb-4">Compose Message</h4>
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading...</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div>
            <Button variant="link" onClick={() => navigate('/cms/standard-messages')} className="mb-3 ps-0">
                <ArrowLeft size={18} className="me-2" />
                Back to Templates
            </Button>
            <h4 className="mb-4">Compose Message</h4>

            <div className="row">
                {/* Form */}
                <div className="col-md-5">
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white border-bottom">
                            <strong>Message Configuration</strong>
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Select Template <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={selectedTemplate}
                                        onChange={(e) => setSelectedTemplate(parseInt(e.target.value))}
                                    >
                                        {templates.map((template) => (
                                            <option key={template.id} value={template.id}>
                                                {template.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Select Reservation <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={selectedReservation}
                                        onChange={(e) => setSelectedReservation(e.target.value)}
                                    >
                                        <option value="">Choose a reservation...</option>
                                        {reservations.map((reservation) => {
                                            const house = houses.find(h => h.public_id === reservation.house_id);
                                            return (
                                                <option key={reservation.public_id} value={reservation.public_id}>
                                                    {reservation.name_first_guess} - {house?.name || house?.address || 'Unknown'} ({new Date(reservation.check_in_date).toLocaleDateString()})
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Language <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={selectedLanguage}
                                        onChange={(e) => setSelectedLanguage(e.target.value)}
                                    >
                                        <option value="es">🇪🇸 Spanish</option>
                                        <option value="en">🇬🇧 English</option>
                                        <option value="nl">🇳🇱 Dutch</option>
                                        <option value="fr">🇫🇷 French</option>
                                    </Form.Select>
                                </Form.Group>

                                <Button
                                    variant="primary"
                                    onClick={handleGenerateMessage}
                                    disabled={generating || !selectedTemplate || !selectedReservation}
                                    className="w-100"
                                >
                                    {generating ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Generating...
                                        </>
                                    ) : (
                                        'Generate Message'
                                    )}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>

                    {/* Variable Info */}
                    {selectedReservation && (
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <strong>Variables Preview</strong>
                            </Card.Header>
                            <Card.Body>
                                {(() => {
                                    const reservation = reservations.find(r => r.public_id === selectedReservation);
                                    const house = houses.find(h => h.public_id === reservation?.house_id);
                                    if (!reservation || !house) return null;

                                    const vars = standardMessageService.buildVariablesFromReservation(reservation, house);
                                    return (
                                        <div>
                                            {Object.entries(vars).map(([key, value]) => (
                                                <div key={key} className="mb-2">
                                                    <Badge bg="secondary" className="me-2">
                                                        {`{{${key}}}`}
                                                    </Badge>
                                                    <span style={{ fontSize: '0.9rem' }}>{value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </Card.Body>
                        </Card>
                    )}
                </div>

                {/* Generated Message */}
                <div className="col-md-7">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                            <strong>Generated Message</strong>
                            {generatedMessage && (
                                <Button
                                    size="sm"
                                    variant={copied ? 'success' : 'outline-primary'}
                                    onClick={handleCopyMessage}
                                >
                                    {copied ? (
                                        <>
                                            <Check size={14} className="me-1" />
                                            Copied!
                                        </>
                                    ) : (
                                        <>
                                            <Copy size={14} className="me-1" />
                                            Copy
                                        </>
                                    )}
                                </Button>
                            )}
                        </Card.Header>
                        <Card.Body>
                            {generatedMessage ? (
                                <div
                                    style={{
                                        backgroundColor: '#f8f9fa',
                                        padding: '20px',
                                        borderRadius: '8px',
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        fontSize: '1rem',
                                        lineHeight: '1.6',
                                        minHeight: '400px',
                                    }}
                                >
                                    {generatedMessage}
                                </div>
                            ) : (
                                <div className="text-center py-5 text-muted">
                                    <p>Select a template and reservation, then click "Generate Message" to see the result here.</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default MessageComposer;
